import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { trackPageView } from "@/utils/metaTracking";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import EventsPage from "@/pages/EventsPage";
import EventLandingPage from "@/pages/EventLandingPage";
import EventRegisterPage from "@/pages/EventRegisterPage";
import EventSuccessPage from "@/pages/EventSuccessPage";
import ExperiencesPage from "@/pages/ExperiencesPage";
import MocktailsPage from "@/pages/MocktailsPage";
import GalleryPage from "@/pages/GalleryPage";
import BookingPage from "@/pages/BookingPage";
import BookingSuccessPage from "@/pages/BookingSuccessPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import AboutPage from "@/pages/AboutPage";
import DynamicPage from "@/pages/DynamicPage";
import DynamicEventLandingPage from "@/pages/DynamicEventLandingPage";
// Admin Pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminEventsPage from "@/pages/admin/AdminEventsPage";
import AdminBookingsPage from "@/pages/admin/AdminBookingsPage";
import AdminCouponsPage from "@/pages/admin/AdminCouponsPage";
import AdminGuestsPage from "@/pages/admin/AdminGuestsPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";
import AdminPackagesPage from "@/pages/admin/AdminPackagesPage";
import AdminWebsitePage from "@/pages/admin/AdminWebsitePage";
import AdminGalleryPage from "@/pages/admin/AdminGalleryPage";
import AdminMessagesPage from "@/pages/admin/AdminMessagesPage";
import AdminPagesPage from "@/pages/admin/AdminPagesPage";
import AdminLandingPages from "@/pages/admin/AdminLandingPages";
import LandingPageEditor from "@/pages/admin/LandingPageEditor";
import VisualPageBuilder from "@/pages/admin/VisualPageBuilder";
import SEODashboard from "@/pages/admin/SEODashboard";
import SEOKeywordResearch from "@/pages/admin/SEOKeywordResearch";
import SEOContentOptimizer from "@/pages/admin/SEOContentOptimizer";
import SEOMetaGenerator from "@/pages/admin/SEOMetaGenerator";
// Analytics Pages
import ConversionsDashboard from "@/pages/admin/ConversionsDashboard";
import HeatmapViewer from "@/pages/admin/HeatmapViewer";
import ABTestMonitor from "@/pages/admin/ABTestMonitor";
import PixelSettings from "@/pages/admin/PixelSettings";
import EmailSettings from "@/pages/admin/EmailSettings";
import GuestManagement from "@/pages/admin/GuestManagement";
import TreatsPage from "@/pages/TreatsPage";
import MiniMakersWaitlist from "@/pages/MiniMakersWaitlist";
import MarketCampPage from "@/pages/MarketCampPage";
import MarketCampSuccess from "@/pages/MarketCampSuccess";
import MarketCampPoliciesPage from "@/pages/MarketCampPoliciesPage";
import AdminTreatsInquiries from "@/pages/admin/AdminTreatsInquiries";
import AdminTreatsImages from "@/pages/admin/AdminTreatsImages";
import AdminTreatsGallery from "@/pages/admin/AdminTreatsGallery";
import AdminMiniMakers from "@/pages/admin/AdminMiniMakers";
import AdminMarketCampRegistrations from "@/pages/admin/AdminMarketCampRegistrations";
import AdminMarketCampSettings from "@/pages/admin/AdminMarketCampSettings";
import AdminMarketCampTickets from "@/pages/admin/AdminMarketCampTickets";
import AdminMarketCampGroups from "@/pages/admin/AdminMarketCampGroups";
import AdminMarketCampReferrals from "@/pages/admin/AdminMarketCampReferrals";
import AdminMarketCampAmbassadors from "@/pages/admin/AdminMarketCampAmbassadors";
import AdminMarketCampCoupons from "@/pages/admin/AdminMarketCampCoupons";
import ChatWidget from "@/components/ChatWidget";

// Public Layout wrapper
const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
    <ChatWidget />
  </>
);

// Meta Pixel: fire PageView on every SPA route change
const RouteChangeTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);
  return null;
};

function App() {
  return (
    <div className="App min-h-screen bg-[#050505]">
      <BrowserRouter>
        <RouteChangeTracker />
        <Routes>
          {/* Admin Routes (no navbar/footer) */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/events" element={<AdminEventsPage />} />
          <Route path="/admin/bookings" element={<AdminBookingsPage />} />
          <Route path="/admin/coupons" element={<AdminCouponsPage />} />
          <Route path="/admin/guests" element={<AdminGuestsPage />} />
          <Route path="/admin/guest-management" element={<GuestManagement />} />
          <Route path="/admin/packages" element={<AdminPackagesPage />} />
          <Route path="/admin/gallery" element={<AdminGalleryPage />} />
          <Route path="/admin/website" element={<AdminWebsitePage />} />
          <Route path="/admin/pages" element={<AdminPagesPage />} />
          <Route path="/admin/pages/:pageId/edit" element={<VisualPageBuilder />} />
          <Route path="/admin/landing-pages" element={<AdminLandingPages />} />
          <Route path="/admin/landing-pages/:pageId" element={<LandingPageEditor />} />
          <Route path="/admin/messages" element={<AdminMessagesPage />} />
          <Route path="/admin/settings" element={<AdminSettingsPage />} />
          {/* SEO Routes */}
          <Route path="/admin/seo" element={<SEODashboard />} />
          <Route path="/admin/seo/keywords" element={<SEOKeywordResearch />} />
          <Route path="/admin/seo/optimizer" element={<SEOContentOptimizer />} />
          <Route path="/admin/seo/meta" element={<SEOMetaGenerator />} />
          {/* Analytics Routes */}
          <Route path="/admin/conversions" element={<ConversionsDashboard />} />
          <Route path="/admin/heatmap/:slug" element={<HeatmapViewer />} />
          <Route path="/admin/ab-tests" element={<ABTestMonitor />} />
          <Route path="/admin/ab-test/:testId" element={<ABTestMonitor />} />
          <Route path="/admin/pixel-settings" element={<PixelSettings />} />
          <Route path="/admin/email-settings" element={<EmailSettings />} />
          <Route path="/admin/treats-inquiries" element={<AdminTreatsInquiries />} />
          <Route path="/admin/treats-images" element={<AdminTreatsImages />} />
          <Route path="/admin/treats-gallery" element={<AdminTreatsGallery />} />
          <Route path="/admin/mini-makers" element={<AdminMiniMakers />} />
          <Route path="/admin/market-camp-registrations" element={<AdminMarketCampRegistrations />} />
          <Route path="/admin/market-camp-tickets" element={<AdminMarketCampTickets />} />
          <Route path="/admin/market-camp-groups" element={<AdminMarketCampGroups />} />
          <Route path="/admin/market-camp-referrals" element={<AdminMarketCampReferrals />} />
          <Route path="/admin/market-camp-ambassadors" element={<AdminMarketCampAmbassadors />} />
          <Route path="/admin/market-camp-coupons" element={<AdminMarketCampCoupons />} />
          <Route path="/admin/market-camp-settings" element={<AdminMarketCampSettings />} />
          
          {/* Public Routes with Layout */}
          <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
          <Route path="/experiences" element={<PublicLayout><ExperiencesPage /></PublicLayout>} />
          <Route path="/events" element={<PublicLayout><EventsPage /></PublicLayout>} />
          <Route path="/events/:eventId" element={<PublicLayout><EventLandingPage /></PublicLayout>} />
          <Route path="/events/:eventId/register" element={<PublicLayout><EventRegisterPage /></PublicLayout>} />
          <Route path="/events/success" element={<PublicLayout><EventSuccessPage /></PublicLayout>} />
          <Route path="/gallery" element={<PublicLayout><GalleryPage /></PublicLayout>} />
          <Route path="/booking" element={<PublicLayout><BookingPage /></PublicLayout>} />
          <Route path="/booking/success" element={<PublicLayout><BookingSuccessPage /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
          <Route path="/treats" element={<PublicLayout><TreatsPage /></PublicLayout>} />
          <Route path="/mini-makers-waitlist" element={<MiniMakersWaitlist />} />
          <Route path="/mini-makers" element={<MiniMakersWaitlist />} />
          <Route path="/mini-makers-market-camp" element={<MarketCampPage />} />
          <Route path="/mini-makers-market-camp/success" element={<MarketCampSuccess />} />
          <Route path="/mini-makers-market-camp/policies" element={<MarketCampPoliciesPage />} />
          <Route path="/mocktails" element={<PublicLayout><MocktailsPage /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
          <Route path="/privacy" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
          <Route path="/terms" element={<PublicLayout><TermsPage /></PublicLayout>} />
          <Route path="/page/:slug" element={<PublicLayout><DynamicPage /></PublicLayout>} />
          {/* Dynamic Event Landing Pages - supports any slug */}
          <Route path="/suds-on-the-vine" element={<DynamicEventLandingPage />} />
          <Route path="/suds-1" element={<DynamicEventLandingPage />} />
          <Route path="/event/:slug" element={<DynamicEventLandingPage />} />
          {/* Catch-all for any landing page slug */}
          <Route path="/:landingSlug" element={<DynamicEventLandingPage />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
